import { createClient, SupabaseClient } from "@supabase/supabase-js";
import type { AccountErrorKind, AccountServices } from "../../core/account/types";
import {
  linkErrorKind,
  SupabaseAccountService,
  SupabasePortfolioStore,
  SupabasePriceStore,
  SupabaseSettingsStore,
} from "./supabaseAccount";

// The only file that creates the Supabase client (specs/11). The URL and publishable key are
// public by design (FR-73); the secret / service_role key never goes in the app.

const url = process.env.REACT_APP_SUPABASE_URL;
const key = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

/**
 * Where invite, reset and Google links come back to: the site's root, so Supabase's redirect
 * allow-list stays a fixed list of two addresses (FR-74). The app then routes on `type`.
 */
const appUrl = () => `${window.location.origin}${process.env.PUBLIC_URL}`;

export interface LinkParams {
  /** "invite", "recovery", … from a link that signed the person in. */
  type: string | null;
  error: AccountErrorKind | null;
}

/**
 * Reads what an invite, reset or Google link brought, before supabase-js sees the URL. An error
 * is removed from the address bar here: supabase-js only clears successful links.
 */
function takeLinkParams(): LinkParams {
  const address = new URL(window.location.href);
  const hash = new URLSearchParams(address.hash.slice(1));
  const get = (name: string) => hash.get(name) ?? address.searchParams.get(name);

  const error = linkErrorKind(get("error"), get("error_code"), get("error_description"));
  if (error) {
    for (const name of ["error", "error_code", "error_description", "sb"]) {
      hash.delete(name);
      address.searchParams.delete(name);
    }
    address.hash = hash.toString();
    window.history.replaceState(window.history.state, "", address.toString());
  }
  return { type: get("access_token") ? get("type") : null, error };
}

const link = url && key ? takeLinkParams() : null;

export const supabase: SupabaseClient | null =
  url && key
    ? createClient(url, key, {
        // supabase-js keeps the session in localStorage, the one thing the app stores in the
        // browser (D10). Invite and reset links use the implicit flow, so this does too.
        auth: { flowType: "implicit", persistSession: true, detectSessionInUrl: true },
      })
    : null;

/** null when the build has no Supabase settings: the app then shows a configuration error. */
export const webServices: AccountServices | null =
  supabase && link
    ? {
        account: new SupabaseAccountService(supabase, link, appUrl),
        stores: (account) => ({
          portfolio: new SupabasePortfolioStore(supabase, account.id),
          settings: new SupabaseSettingsStore(supabase, account.id),
          prices: new SupabasePriceStore(supabase),
        }),
      }
    : null;

/** The signed-in person's access token, for the card-reading relay (specs/12 FR-86). */
export async function accessToken({ refresh = false } = {}): Promise<string | null> {
  if (!supabase) return null;
  const { data } = refresh
    ? await supabase.auth.refreshSession()
    : await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
